import React, {useState} from 'react'
import axios from 'axios'
import toaster from '../../../../Utils/toaster'
import './VosRatingPanel.css'

const API_URL = process.env.REACT_APP_API_URL

export type VosRatingPanelProps = {
  appointmentId: number
  token?: string
  staffMemberName?: string | null
  staffMemberEmail?: string | null
  onSkipped: () => void
  onSubmitted: () => void
}

/**
 * Optional Digital VOS requester rating (interpreter stars + comment).
 * Visual pattern inspired by FeedbackPanel; fields match call-log interpreter rating only.
 */
const VosRatingPanel: React.FC<VosRatingPanelProps> = ({
  appointmentId,
  token,
  staffMemberName,
  staffMemberEmail,
  onSkipped,
  onSubmitted,
}) => {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5) {
      toaster('error', 'Please rate the interpreter (1–5 stars).')
      return
    }

    try {
      setIsSubmitting(true)
      const body: {
        appointmentId: number
        interpreterRating: number
        interpreterComment?: string
        token?: string
        staffMemberName?: string
        staffMemberEmail?: string
      } = {
        appointmentId,
        interpreterRating: rating,
        interpreterComment: comment.trim() || undefined,
      }
      if (token) {
        body.token = token
        if (staffMemberName?.trim()) body.staffMemberName = staffMemberName.trim()
        if (staffMemberEmail?.trim()) body.staffMemberEmail = staffMemberEmail.trim()
      }

      const res = await axios.post(`${API_URL}/Appoinment/vos-rating`, body, {withCredentials: true})
      const status = res.data?.status ?? res.data?.Status
      if (status === 'E') {
        toaster('error', res.data?.text ?? res.data?.Text ?? 'Failed to submit rating.')
        return
      }

      toaster('success', 'Thank you for your feedback!')
      setSubmitted(true)
      onSubmitted()
    } catch (err: unknown) {
      const message =
        (err as {response?: {data?: {text?: string; Text?: string}}})?.response?.data?.text ??
        (err as {response?: {data?: {text?: string; Text?: string}}})?.response?.data?.Text ??
        'Failed to submit rating. Please try again.'
      console.error('VosRatingPanel submit error:', err)
      toaster('error', message)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className='vos-rating-panel vos-rating-success d-flex flex-column align-items-center justify-content-center'>
        <i className='bi bi-check-circle-fill text-success fs-1 mb-3'></i>
        <h5 className='text-center mb-0'>Feedback Submitted!</h5>
      </div>
    )
  }

  return (
    <div className='vos-rating-panel'>
      <div className='vos-rating-header'>
        <div className='vos-rating-title'>
          <i className='bi bi-star-fill me-2 text-white'></i>
          <span>Rating &amp; Feedback</span>
        </div>
      </div>

      <div className='vos-rating-contents'>
        <p className='text-muted mb-3'>
          Optional — rate your interpreter for this onsite appointment. You can skip this step.
        </p>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Rate the Interpreter</label>
          <div className='d-flex gap-2'>
            {[1, 2, 3, 4, 5].map((star) => (
              <i
                key={star}
                className={`bi bi-star-fill fs-3 ${star <= rating ? 'text-warning' : 'text-muted'}`}
                onClick={() => !isSubmitting && setRating(star)}
                style={{cursor: isSubmitting ? 'default' : 'pointer'}}
                role='button'
                aria-label={`${star} star${star === 1 ? '' : 's'}`}
              ></i>
            ))}
          </div>
        </div>

        <div className='mb-4'>
          <label className='form-label fw-bold'>Add Comments</label>
          <textarea
            className='form-control'
            placeholder='Tell us more about your experience (optional)'
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={isSubmitting}
            maxLength={1000}
          />
        </div>

        <div className='d-flex flex-column flex-sm-row gap-2'>
          <button
            type='button'
            className='btn btn-light flex-fill'
            onClick={onSkipped}
            disabled={isSubmitting}
          >
            Skip
          </button>
          <button
            type='button'
            className='btn btn-warning rounded-pill flex-fill'
            style={{backgroundColor: '#e58831', borderColor: '#e58831', color: '#fff'}}
            onClick={handleSubmit}
            disabled={isSubmitting || rating < 1}
          >
            {isSubmitting ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VosRatingPanel
